import React, { useEffect, useState } from 'react'
import { Autocomplete as MuiAutocomplete, Box, Chip } from '@mui/material'
import { styled } from '@mui/material/styles'
import DefaultPaper from './Paper'
import DefaultListbox from './Listbox'
import DefaultLabel from './Label'
import DefaultOption from './Option'
import DefaultTag from './Tag'
import DefaultInputWrapper from './InputWrapper'
import DefaultEndAdornmentContainer from './EndAdornmentContainer'
import Spinner from '../Spinner'
import Checkbox from '../Checkbox'
import { isNilOrEmpty } from 'packages/core'
import InfoTooltip from '../ApplicationFieldTypes/Components/InfoTooltip'
import { useTranslation } from 'react-i18next'
import { getInfoTooltipFieldsConfig, getInfoTooltipItems } from 'packages/core'

const DEFAULT_INFO_TOOLTIP_FIELDS = [
    { field: 'FriendlyName', labelKey: 'Common_FriendlyName' },
    { field: 'Description', labelKey: 'Common_Description' },
]

const fullWidthStyles = { width: '100%' }

const StyledAutocomplete = styled(MuiAutocomplete)(
    ({ theme, rootStyleProps }) => ({
        width: '100%',
        color: '#73767A',
        fontSize: '1.6rem',
        '& > div': {
            transition: 'border 0.15s ease-in-out 0s',
        },
        ...rootStyleProps,

        '& .MuiInputBase-input': {
            height: '3.6rem',
        },
        '& input::placeholder': {
            color: '#b4b4b4',
            opacity: 1,
        },
        '&.Mui-focused > div': {
            borderColor: theme.palette.primary.main,
        },
    }),
)

const StyledOption = styled('li')(({ theme }) => ({
    width: 'max-content',
    minWidth: '100%',
}))

function Autocomplete(props) {
    const infoTooltipFieldsConfig = (() => {
        const configFields = getInfoTooltipFieldsConfig()
        return configFields.length > 0 ? configFields : DEFAULT_INFO_TOOLTIP_FIELDS
    })()
    const {
        label,
        loading,
        options,
        getOptionLabel,
        disabled,
        placeholder,
        labelComponent: Label,
        optionComponent: Option,
        tagComponent: Tag,
        inputWrapperComponent: InputWrapper,
        endAdornmentContainerComponent: EndAdornmentContainer,
        endAdornmentComponent: EndAdornment,
        clearIcon,
        value,
        rootStyleProps,
        multiselect,
        chipsConfig,
        inputStyleProps,
        popperStyleProps,
        getOptionSubLabel,
        showOptionSubLabel,
        inputWrapperStyles,
        showInfoTooltip = false,
        ...rest
    } = props

    const { t } = useTranslation()
    const [localValue, setLocalValue] = useState([])

    useEffect(() => {
        if (multiselect && isNilOrEmpty(value)) {
            setLocalValue([])
        } else {
            setLocalValue(value)
        }
    }, [value, multiselect])

    return (
        <StyledAutocomplete
            multiple={multiselect}
            loadingText=""
            noOptionsText=""
            disablePortal={false}
            loading={loading}
            options={options}
            getOptionLabel={getOptionLabel}
            disabled={disabled}
            disableCloseOnSelect={multiselect}
            filterOptions={(options) => options}
            value={localValue}
            rootStyleProps={rootStyleProps}
            openOnFocus={true}
            PopperProps={{
                sx: popperStyleProps,
            }}
            renderOption={(propsOption, option, { selected }) => {
                const newProps = {
                    ...propsOption,
                    key: option.id ? option.id : propsOption.key,
                }
                return (
                    <StyledOption {...newProps}>
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                color: '#73767A',
                            }}
                        >
                            {multiselect && <Checkbox checked={selected} />}
                            <Option
                                option={option}
                                optionState={{ selected }}
                                getOptionLabel={getOptionLabel}
                                showOptionSubLabel={showOptionSubLabel}
                                getOptionSubLabel={getOptionSubLabel}
                            />
                            {showInfoTooltip && (
                                <InfoTooltip
                                    items={getInfoTooltipItems(
                                        option,
                                        infoTooltipFieldsConfig,
                                        t,
                                    )}
                                />
                            )}
                        </Box>
                    </StyledOption>
                )
            }}
            isOptionEqualToValue={(option, value) =>
                getOptionLabel(option) === getOptionLabel(value)
            }
            renderTags={(value, getTagProps) =>
                value.map((option, index) =>
                    chipsConfig && chipsConfig.type === 'round' ? (
                        <Chip
                            label={getOptionLabel(option)}
                            size={chipsConfig.size || 'small'}
                            style={{ fontSize: '12px' }}
                            {...getTagProps({ index })}
                        />
                    ) : (
                        <Tag
                            label={getOptionLabel(option)}
                            {...getTagProps({ index })}
                        />
                    ),
                )
            }
            renderInput={(params) => (
                <>
                    {label && (
                        <Label
                            style={{ color: '#73767A' }}
                            {...params.InputLabelProps}
                        >
                            {label}
                        </Label>
                    )}
                    <InputWrapper
                        ref={params.InputProps.ref}
                        style={{
                            paddingRight: (EndAdornment || clearIcon) && '55px',
                            ...inputWrapperStyles,
                        }}
                    >
                        {params.InputProps.startAdornment}
                        <input
                            {...params.inputProps}
                            placeholder={placeholder}
                            style={{
                                ...fullWidthStyles,
                                ...inputStyleProps,
                            }}
                        />
                        {loading && (
                            <EndAdornmentContainer>
                                <Spinner size={0.7} color="#453fbb" />
                            </EndAdornmentContainer>
                        )}
                        {!loading && EndAdornment && (
                            <EndAdornmentContainer>
                                <EndAdornment />
                            </EndAdornmentContainer>
                        )}
                        {!loading && clearIcon && (
                            <EndAdornmentContainer>
                                {clearIcon}
                            </EndAdornmentContainer>
                        )}
                    </InputWrapper>
                </>
            )}
            PaperComponent={DefaultPaper}
            ListboxComponent={DefaultListbox}
            {...rest}
        />
    )
}

Autocomplete.defaultProps = {
    PaperComponent: DefaultPaper,
    ListboxComponent: DefaultListbox,
    labelComponent: DefaultLabel,
    optionComponent: DefaultOption,
    tagComponent: DefaultTag,
    inputWrapperComponent: DefaultInputWrapper,
    endAdornmentContainerComponent: DefaultEndAdornmentContainer,
}

Autocomplete.PaperComponent = DefaultPaper
Autocomplete.ListboxComponent = DefaultListbox
Autocomplete.labelComponent = DefaultLabel
Autocomplete.optionComponent = DefaultOption
Autocomplete.tagComponent = DefaultTag
Autocomplete.inputWrapperComponent = DefaultInputWrapper
Autocomplete.endAdornmentContainerComponent = DefaultEndAdornmentContainer

export default Autocomplete
